import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { AppSelect } from "components/common/inputs";
import { AppButton } from "components/common/buttons";

const getInitialValues = (fields, initialValues) =>
  fields.reduce((values, field) => {
    if (field.type === "switch") {
      values[field.name] = initialValues?.[field.name] ?? field.defaultValue ?? false;
    } else if (field.type === "select" && field.multiple) {
      const initialValue = initialValues?.[field.name] ?? field.defaultValue ?? [];
      values[field.name] = Array.isArray(initialValue) ? initialValue : initialValue ? [initialValue] : [];
    } else {
      values[field.name] = initialValues?.[field.name] ?? field.defaultValue ?? "";
    }
    return values;
  }, {});

const CatalogFormDialog = ({
  open,
  title,
  fields,
  initialValues,
  submitting,
  onClose,
  onBeforeChange,
  onSubmit,
}) => {
  const initial = useMemo(() => getInitialValues(fields, initialValues), [fields, initialValues]);
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setValues(initial);
      setErrors({});
    }
  }, [initial, open]);

  const handleChange = (name, value) => {
    const nextValues = { ...values, [name]: value };
    if (onBeforeChange?.(name, value, nextValues, values) === false) return;
    setValues(nextValues);
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = () => {
    const nextErrors = {};
    fields.forEach((field) => {
      const value = values[field.name];
      const isEmptyMultiple = field.multiple && Array.isArray(value) && value.length === 0;
      const isEmptySingle = !field.multiple && !String(value ?? "").trim();
      if (field.required && (isEmptyMultiple || isEmptySingle)) {
        nextErrors[field.name] = "Vui lòng nhập thông tin";
      }
    });

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onSubmit(values);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ elevation: 0 }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {fields.map((field) => {
            if (field.type === "select") {
              return (
                <AppSelect
                  key={field.name}
                  label={field.label}
                  value={values[field.name] ?? (field.multiple ? [] : "")}
                  options={field.options}
                  required={field.required}
                  disabled={submitting || field.disabled}
                  error={Boolean(errors[field.name])}
                  helperText={errors[field.name]}
                  multiple={Boolean(field.multiple)}
                  onChange={(selectedValue) => handleChange(field.name, selectedValue)}
                />
              );
            }

            if (field.type === "switch") {
              return (
                <FormControlLabel
                  key={field.name}
                  control={
                    <Switch
                      checked={Boolean(values[field.name])}
                      onChange={(event) => handleChange(field.name, event.target.checked)}
                      disabled={submitting || field.disabled}
                    />
                  }
                  label={field.label}
                />
              );
            }

            return (
              <TextField
                key={field.name}
                name={field.name}
                label={field.label}
                value={values[field.name] ?? ""}
                onChange={(event) => handleChange(field.name, event.target.value)}
                required={field.required}
                error={Boolean(errors[field.name])}
                helperText={errors[field.name]}
                disabled={submitting || field.disabled}
                multiline={field.multiline}
                minRows={field.multiline ? 2 : undefined}
                type={field.type === "number" ? "number" : "text"}
                size="small"
                fullWidth
              />
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        {/* <Button onClick={onClose} disabled={submitting}>
          Hủy
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting} disableElevation>
          {submitting ? <CircularProgress size={22} /> : "Lưu"}
        </Button> */}
        <AppButton onClick={onClose} disabled={submitting}>
          Hủy
        </AppButton>
        <AppButton onClick={handleSubmit} variant="contained" disabled={submitting} disableElevation>
          {submitting ? <CircularProgress size={22} /> : "Lưu"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogFormDialog;
